import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Loading from "../../components/Loading";
import Meta from '../../components/Meta';
import ReCaptcha from '../../components/ReCaptcha';
import PasswordRequirements from '../../components/PasswordRequirements';
import AppContext from "../../../context";

import Data from '../../../repository';
import { createAccountSchema } from '../../../validation/schemas';

import styles from "../../../styles/Entry.module.scss";

function CreateAccount() {
	const context = useContext(AppContext);
	const [loading, setLoading] = useState(false);
	const [serverError, setServerError] = useState(null);
	const [recaptchaToken, setRecaptchaToken] = useState(null);

	const {
		register,
		handleSubmit,
		formState: { errors },
		watch
	} = useForm({
		resolver: yupResolver(createAccountSchema),
		defaultValues: {
			email: '',
			password: '',
			firstName: '',
			lastName: '',
			phoneNumber: '',
			subscribe: false
		}
	});

	const onRecaptchaChange = (token) => {
		setRecaptchaToken(token);
		if (token) {
			setServerError(null);
		}
	}

	const onSubmit = async (data) => {
		// Validate reCAPTCHA
		if (process.env.REACT_APP_RECAPTCHA_ENABLED === 'true' && !recaptchaToken) {
			setServerError("Please complete the reCAPTCHA verification.");
			return;
		}

		setLoading(true);
		setServerError(null);

		try {
			await Data.createAccount({ ...data, recaptchaToken });
			const result = await Data.login(data.email, data.password);
			localStorage.setItem("token", result.data.token);
			window.location.reload();
		} catch (error) {
			setServerError(error.response?.data?.message || 'An error occurred. Please try again.');
			setRecaptchaToken(null);
		} finally {
			setLoading(false);
		}
	}

	return (
		<>
			<Meta title="Create Account" />

			<div>
				{serverError &&
					<div className={styles.error}>
						<p>{serverError}</p>
					</div>
				}

				<h1>Create Account</h1>

				<form onSubmit={handleSubmit(onSubmit)}>
					<div>
						<input
							type="email"
							placeholder="Email Address"
							{...register('email')}
							maxLength={255}
							className={errors.email ? 'input-error' : ''}
						/>
						{errors.email && <p className={styles.fieldError}>{errors.email.message}</p>}
					</div>
					<div>
						<input
							type="password"
							placeholder="Password"
							{...register('password')}
							maxLength={255}
							className={errors.password ? 'input-error' : ''}
						/>
						<PasswordRequirements password={watch('password')} />
					{errors.password && <p className={styles.fieldError}>{errors.password.message}</p>}
					</div>
					<div>
						<input
							type="text"
							placeholder="First Name"
							{...register('firstName')}
							maxLength={255}
							className={errors.firstName ? 'input-error' : ''}
						/>
						{errors.firstName && <p className={styles.fieldError}>{errors.firstName.message}</p>}
					</div>
					<div>
						<input
							type="text"
							placeholder="Last Name"
							{...register('lastName')}
							maxLength={255}
							className={errors.lastName ? 'input-error' : ''}
						/>
						{errors.lastName && <p className={styles.fieldError}>{errors.lastName.message}</p>}
					</div>
					<div>
						<input
							type="text"
							placeholder="Phone Number"
							{...register('phoneNumber')}
							maxLength={255}
							className={errors.phoneNumber ? 'input-error' : ''}
						/>
						{errors.phoneNumber && <p className={styles.fieldError}>{errors.phoneNumber.message}</p>}
					</div>
					<label className={styles.checkbox}>
						<input type="checkbox" {...register('subscribe')} />
						Receive news and updates about {context.domain.name}.
					</label>
					<ReCaptcha
						onChange={onRecaptchaChange}
						onExpired={() => onRecaptchaChange(null)}
					/>
					<button className="btn btnPrimary" disabled={loading}>
						{loading ?
							<Loading button={true} />
							:
							`Create Account`
						}
					</button>
				</form>

				<div className={styles.alternatives}>
					<Link to="/entry/login">Already have an account?</Link>
				</div>
			</div>
		</>
	);
}

export default CreateAccount;
